import { motion } from "framer-motion";
import { useLanguage } from "@/i18n/LanguageContext";

const lineAnim = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.85, delay: 0.2 + i * 0.13, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const { t } = useLanguage();
  return (
    <header data-testid="hero-section" className="relative mx-auto w-full max-w-md">
      <div className="relative h-[32vh] min-h-[230px] overflow-hidden">
        <img
          src="/assets/hero.webp"
          alt="球员射门瞬间"
          fetchPriority="high"
          className="h-full w-full object-cover object-[center_18%]"
        />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(11,61,145,0.1)_0%,rgba(245,248,255,0)_55%,#F5F8FF_100%)]" />
      </div>

      <div className="px-6 pb-12 pt-3">
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-4 inline-flex w-fit items-center rounded-full border border-[rgba(22,99,230,0.25)] bg-[rgba(22,99,230,0.07)] px-3.5 py-1.5 text-[11px] tracking-[0.12em] text-[#0B3D91]"
          data-testid="hero-kicker-badge"
        >
          {t("kicker")}
        </motion.p>

        <h1 className="text-4xl font-bold leading-[1.15] tracking-tight text-[#0F1E3D] sm:text-5xl" data-testid="hero-title">
          <span className="block overflow-hidden pb-1">
            <motion.span custom={0} variants={lineAnim} initial="hidden" animate="show" className="block">
              {t("heroLine1")}
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-1">
            <motion.span custom={1} variants={lineAnim} initial="hidden" animate="show" className="accent-text block">
              {t("heroLine2")}
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.55 }}
          className="mt-3 text-sm font-semibold tracking-wide text-[#1663E6]"
          data-testid="hero-subtitle"
        >
          {t("heroTags")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.68 }}
          className="mt-4 flex flex-wrap gap-2"
          data-testid="hero-tag-pills"
        >
          {[t("tag1"), t("tag2"), t("tag3")].map((tag, i) => (
            <span
              key={tag}
              className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-[11px] font-medium ${
                i === 1
                  ? "border-[rgba(242,183,5,0.5)] bg-[rgba(242,183,5,0.12)] text-[#8A6100]"
                  : "border-[rgba(22,99,230,0.25)] bg-[rgba(22,99,230,0.07)] text-[#0B3D91]"
              }`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${i === 1 ? "bg-[#F2B705]" : "bg-[#1663E6]"}`} />
              {tag}
            </span>
          ))}
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.78 }}
          className="mt-3 text-sm leading-relaxed text-slate-500"
          data-testid="hero-desc"
        >
          {t("heroDesc")}
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.88 }}
          className="mt-2.5 flex items-center gap-2 text-[11px] font-medium text-[#0B3D91]"
          data-testid="hero-value-line"
        >
          <span className="h-1 w-6 rounded-full bg-[#F2B705]" />
          {t("valueLine")}
        </motion.p>
      </div>
    </header>
  );
}
